import { motion } from "framer-motion";
import { Clock, Award, Globe2, Rocket, Users, DollarSign } from "lucide-react";

const pillars = [
    {
        icon: Clock,
        number: "24/7",
        title: "Always-on Production",
        desc: "Active design teams running two shifts across regions — so your deadline never sleeps, and neither does progress on your project.",
    },
    {
        icon: Rocket,
        number: "Fast-Track",
        title: "Accelerated Delivery",
        desc: "Built for compressed timelines. Parallel workstreams, integrated BIM, and decision-ready deliverables that keep your project ahead of schedule.",
    },
    {
        icon: DollarSign,
        number: "Best-in-Class",
        title: "Pricing That Makes Sense",
        desc: "Premium engineering shouldn't mean premium-only budgets. Our global delivery model lets us deliver world-class work at a fraction of legacy firm rates.",
    },
    {
        icon: Users,
        number: "Top 1%",
        title: "World-Class Talent",
        desc: "Principal engineers, licensed PEs, LEED APs and BIM specialists — hand-picked from the best engineering communities on the planet.",
    },
];

export default function CapabilityPillars() {
    return (
        <section className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden" data-testid="capability-pillars">
            {/* Layered glow background */}
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-blue-600/10 blur-[120px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                {/* Headline */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.2em] uppercase text-cyan-400 mb-6">
                            <span className="w-8 h-px bg-cyan-400/60" /> Why PES
                        </div>
                        <h2 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
                            World-class engineering.<br />
                            <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-500 bg-clip-text text-transparent">
                                Running 24/7.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 flex items-end">
                        <p className="text-lg text-slate-400 leading-relaxed">
                            We combine the top 1% of global engineering talent with an always-on production
                            model — delivering premium MEP design at pricing that legacy firms can't match,
                            on timelines they can't meet.
                        </p>
                    </div>
                </div>

                {/* 24/7 banner card */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7 }}
                    className="relative p-8 md:p-12 rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-slate-900 border border-blue-500/30 overflow-hidden mb-6 shadow-[0_20px_80px_rgba(37,99,235,0.3)]"
                    data-testid="pillar-247-banner"
                >
                    <div className="absolute inset-0 bg-grid opacity-20" />
                    <div className="absolute -right-20 -top-20 w-[400px] h-[400px] rounded-full bg-cyan-400/20 blur-[100px]" />
                    <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                        <div className="md:col-span-7">
                            <div className="inline-flex items-center gap-2 mb-5">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-400" />
                                </span>
                                <span className="text-xs font-mono tracking-[0.2em] uppercase text-cyan-300">Live now · Production teams active</span>
                            </div>
                            <h3 className="font-heading font-black text-white text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tighter">
                                Your deadline has a dedicated team — in every timezone.
                            </h3>
                            <p className="mt-5 text-base md:text-lg text-blue-100/90 leading-relaxed max-w-xl">
                                Active production studios rotating across regions mean drawings advance while you sleep.
                                Purpose-built for fast-tracked projects where every hour counts.
                            </p>
                        </div>
                        <div className="md:col-span-5 grid grid-cols-3 gap-4">
                            {[
                                { t: "2", l: "Regional Timezones" },
                                { t: "24/7", l: "Coverage" },
                                { t: "< 24h", l: "Response SLA" },
                            ].map((x) => (
                                <div key={x.l} className="rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 p-4 text-center">
                                    <div className="font-heading font-black text-white text-2xl md:text-3xl leading-none">{x.t}</div>
                                    <div className="mt-2 text-[10px] tracking-[0.15em] uppercase text-blue-200">{x.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Four pillar grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {pillars.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="group relative p-8 rounded-2xl bg-slate-900/40 backdrop-blur-xl border border-white/5 hover:border-blue-500/40 transition-all duration-500 overflow-hidden"
                                data-testid={`pillar-${i}`}
                            >
                                <div className="absolute -bottom-16 -right-16 w-40 h-40 rounded-full bg-blue-500/5 blur-2xl group-hover:bg-blue-500/20 transition-colors duration-500" />
                                <div className="relative">
                                    <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-6">
                                        <Icon className="w-5 h-5 text-cyan-400" />
                                    </div>
                                    <div className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter leading-none mb-3">
                                        {p.number}
                                    </div>
                                    <div className="text-sm font-semibold text-cyan-300 mb-3">{p.title}</div>
                                    <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
